import { useState, useEffect } from 'react';
import { Plus, RefreshCw, UserCheck, UserX, Clock, Edit2, Trash2, Users } from 'lucide-react';
import api from '../api/api';

const MONTHS = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
const SHIFTS = { morning: 'Mañana (06-14h)', afternoon: 'Tarde (14-22h)', night: 'Noche (22-06h)' };
const CONTRACTS = { full_time: 'Jornada Completa', part_time: 'Jornada Parcial', temporary: 'Temporal', intern: 'Prácticas' };
const ATT_TYPES = {
  present: { l: 'Presente', cls: 'bg-green-100 text-green-700' },
  absent: { l: 'Ausente', cls: 'bg-red-100 text-red-700' },
  vacation: { l: 'Vacaciones', cls: 'bg-blue-100 text-blue-700' },
  sick: { l: 'Baja Médica', cls: 'bg-orange-100 text-orange-700' },
  holiday: { l: 'Festivo', cls: 'bg-purple-100 text-purple-700' },
};

export default function Personal() {
  const [tab, setTab] = useState('empleados');
  const [employees, setEmployees] = useState([]);
  const [summary, setSummary] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showAttModal, setShowAttModal] = useState(false);
  const [editEmp, setEditEmp] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterDept, setFilterDept] = useState('');
  const [attMonth, setAttMonth] = useState(new Date().getMonth() + 1);
  const [attYear, setAttYear] = useState(new Date().getFullYear());

  const emptyEmp = { full_name: '', position: '', department: '', shift: 'morning', contract_type: 'full_time', start_date: '', phone: '', email: '', notes: '', active: true };
  const [empForm, setEmpForm] = useState(emptyEmp);
  const [attForm, setAttForm] = useState({ employee_id: '', date: new Date().toISOString().split('T')[0], type: 'present', start_time: '', end_time: '', notes: '' });

  const loadAll = async () => {
    setLoading(true);
    try {
      const [empRes, sumRes] = await Promise.all([api.get('/personal/employees'), api.get('/personal/summary')]);
      setEmployees(empRes.data);
      setSummary(sumRes.data);
    } finally { setLoading(false); }
  };

  const loadAttendance = async () => {
    const res = await api.get(`/personal/attendance?month=${attMonth}&year=${attYear}`);
    setAttendance(res.data);
  };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { if (tab === 'asistencia') loadAttendance(); }, [tab, attMonth, attYear]);

  const depts = [...new Set(employees.map(e => e.department).filter(Boolean))];
  const filtered = filterDept ? employees.filter(e => e.department === filterDept) : employees;

  const handleSaveEmp = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editEmp) { await api.put(`/personal/employees/${editEmp.id}`, empForm); }
      else { await api.post('/personal/employees', empForm); }
      setShowModal(false); setEditEmp(null); loadAll();
    } finally { setSaving(false); }
  };

  const handleDeleteEmp = async (id) => {
    if (!window.confirm('¿Eliminar este empleado?')) return;
    await api.delete(`/personal/employees/${id}`);
    loadAll();
  };

  const handleSaveAtt = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post('/personal/attendance', attForm);
      setShowAttModal(false); loadAttendance();
    } finally { setSaving(false); }
  };

  const handleDeleteAtt = async (id) => {
    await api.delete(`/personal/attendance/${id}`);
    loadAttendance();
  };

  const openEdit = (emp) => { setEditEmp(emp); setEmpForm({ ...emp }); setShowModal(true); };
  const openCreate = () => { setEditEmp(null); setEmpForm(emptyEmp); setShowModal(true); };

  return (
    <div className="p-6 md:p-8 page-enter">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>Gestión de Personal</h1>
          <p className="text-sm text-gray-500 mt-0.5">Empleados, turnos y control de presencia</p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadAll} className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500"><RefreshCw size={16} /></button>
          {tab === 'empleados' && <button onClick={openCreate} className="flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-medium px-4 py-2 rounded-lg" data-testid="add-employee-btn"><Plus size={16} /> Nuevo Empleado</button>}
          {tab === 'asistencia' && <button onClick={() => setShowAttModal(true)} className="flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-medium px-4 py-2 rounded-lg" data-testid="add-attendance-btn"><Plus size={16} /> Registrar Asistencia</button>}
        </div>
      </div>

      {/* KPIs */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {[
            { icon: Users, l: 'Total Empleados', v: summary.total_employees, c: '#6b7280' },
            { icon: UserCheck, l: 'Activos', v: summary.active_employees, c: '#16a34a' },
            { icon: UserX, l: 'Ausentes (mes)', v: summary.absent_this_month + summary.sick_this_month, c: '#ef4444' },
            { icon: Clock, l: 'Vacaciones (mes)', v: summary.vacation_this_month, c: '#3b82f6' },
          ].map(s => {
            const Icon = s.icon;
            return (
              <div key={s.l} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ backgroundColor: s.c + '15' }}><Icon size={18} style={{ color: s.c }} /></div>
                <div><p className="text-xs text-gray-500">{s.l}</p><p className="text-xl font-bold" style={{ fontFamily: 'Barlow, sans-serif', color: s.c }}>{s.v}</p></div>
              </div>
            );
          })}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {[{ k: 'empleados', l: `Empleados (${employees.length})` }, { k: 'asistencia', l: 'Registro de Asistencia' }].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.k ? 'border-[#16a34a] text-[#16a34a]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{t.l}</button>
        ))}
      </div>

      {tab === 'empleados' && (
        <div>
          {/* Dept filter */}
          <div className="flex gap-2 mb-4 flex-wrap">
            <button onClick={() => setFilterDept('')} className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${!filterDept ? 'bg-[#16a34a] text-white border-[#16a34a]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>Todos los departamentos</button>
            {depts.map(d => <button key={d} onClick={() => setFilterDept(d)} className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${filterDept === d ? 'bg-gray-700 text-white border-gray-700' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>{d}</button>)}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {loading ? <div className="flex justify-center items-center h-40"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#16a34a]" /></div> : (
              <table className="w-full text-sm">
                <thead><tr className="bg-gray-50 border-b border-gray-200">
                  {['Código', 'Nombre', 'Puesto', 'Departamento', 'Turno', 'Contrato', 'Teléfono', 'Estado', ''].map(h => (
                    <th key={h} className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>
                  ))}
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.length === 0 ? <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-400 text-sm">Sin empleados registrados</td></tr>
                    : filtered.map(emp => (
                      <tr key={emp.id} className="table-row-hover" data-testid={`emp-row-${emp.id}`}>
                        <td className="px-3 py-2.5 font-mono text-xs text-gray-500">{emp.code}</td>
                        <td className="px-3 py-2.5 font-semibold text-gray-900 whitespace-nowrap">{emp.full_name}</td>
                        <td className="px-3 py-2.5 text-xs text-gray-700">{emp.position}</td>
                        <td className="px-3 py-2.5 text-xs text-gray-600">{emp.department || '—'}</td>
                        <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">{SHIFTS[emp.shift] || emp.shift}</td>
                        <td className="px-3 py-2.5 text-xs text-gray-600">{CONTRACTS[emp.contract_type] || emp.contract_type}</td>
                        <td className="px-3 py-2.5 text-xs text-gray-500 font-mono">{emp.phone || '—'}</td>
                        <td className="px-3 py-2.5">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${emp.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{emp.active ? 'Activo' : 'Inactivo'}</span>
                        </td>
                        <td className="px-3 py-2.5 flex gap-1">
                          <button onClick={() => openEdit(emp)} className="p-1 text-gray-400 hover:text-blue-600 rounded" data-testid={`edit-emp-${emp.id}`}><Edit2 size={14} /></button>
                          <button onClick={() => handleDeleteEmp(emp.id)} className="p-1 text-gray-400 hover:text-red-500 rounded" data-testid={`delete-emp-${emp.id}`}><Trash2 size={14} /></button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {tab === 'asistencia' && (
        <div>
          <div className="flex gap-3 mb-4 items-center">
            <select value={attMonth} onChange={e => setAttMonth(Number(e.target.value))} className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-[#16a34a] focus:outline-none">
              {MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
            <select value={attYear} onChange={e => setAttYear(Number(e.target.value))} className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-[#16a34a] focus:outline-none">
              {[2026, 2025, 2024].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <span className="text-sm text-gray-500">{attendance.length} registros</span>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50 border-b border-gray-200">
                {['Fecha', 'Empleado', 'Tipo', 'Entrada', 'Salida', 'Notas', ''].map(h => (
                  <th key={h} className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>
                ))}
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {attendance.length === 0 ? <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400 text-sm">Sin registros para este período</td></tr>
                  : attendance.map(att => {
                    const emp = employees.find(e => e.id === att.employee_id);
                    const type = ATT_TYPES[att.type] || ATT_TYPES.present;
                    return (
                      <tr key={att.id} className="table-row-hover" data-testid={`att-row-${att.id}`}>
                        <td className="px-3 py-2.5 font-mono text-xs text-gray-600">{att.date}</td>
                        <td className="px-3 py-2.5 text-sm font-medium text-gray-900">{emp?.full_name || att.employee_id}</td>
                        <td className="px-3 py-2.5"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${type.cls}`}>{type.l}</span></td>
                        <td className="px-3 py-2.5 font-mono text-xs text-gray-600">{att.start_time || '—'}</td>
                        <td className="px-3 py-2.5 font-mono text-xs text-gray-600">{att.end_time || '—'}</td>
                        <td className="px-3 py-2.5 text-xs text-gray-400 max-w-xs truncate">{att.notes || '—'}</td>
                        <td className="px-3 py-2.5"><button onClick={() => handleDeleteAtt(att.id)} className="p-1 text-gray-300 hover:text-red-500 rounded" data-testid={`delete-att-${att.id}`}><Trash2 size={13} /></button></td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Employee Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white z-10">
              <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>{editEmp ? 'Editar Empleado' : 'Nuevo Empleado'}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <form onSubmit={handleSaveEmp} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><label className="block text-xs font-medium text-gray-700 mb-1">Nombre completo *</label>
                  <input type="text" required value={empForm.full_name} onChange={e => setEmpForm({ ...empForm, full_name: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none" /></div>
                <div><label className="block text-xs font-medium text-gray-700 mb-1">Puesto *</label>
                  <input type="text" required value={empForm.position} onChange={e => setEmpForm({ ...empForm, position: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none" /></div>
                <div><label className="block text-xs font-medium text-gray-700 mb-1">Departamento</label>
                  <input type="text" value={empForm.department} onChange={e => setEmpForm({ ...empForm, department: e.target.value })} placeholder="Mantenimiento..." className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none" /></div>
                <div><label className="block text-xs font-medium text-gray-700 mb-1">Turno</label>
                  <select value={empForm.shift} onChange={e => setEmpForm({ ...empForm, shift: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none bg-white">
                    {Object.entries(SHIFTS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select></div>
                <div><label className="block text-xs font-medium text-gray-700 mb-1">Tipo de contrato</label>
                  <select value={empForm.contract_type} onChange={e => setEmpForm({ ...empForm, contract_type: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none bg-white">
                    {Object.entries(CONTRACTS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select></div>
                <div><label className="block text-xs font-medium text-gray-700 mb-1">Fecha incorporación</label>
                  <input type="date" value={empForm.start_date} onChange={e => setEmpForm({ ...empForm, start_date: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none" /></div>
                <div><label className="block text-xs font-medium text-gray-700 mb-1">Teléfono</label>
                  <input type="text" value={empForm.phone} onChange={e => setEmpForm({ ...empForm, phone: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none" /></div>
                <div><label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" value={empForm.email} onChange={e => setEmpForm({ ...empForm, email: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none" /></div>
                <div className="col-span-2 flex items-center gap-3">
                  <input type="checkbox" id="active" checked={empForm.active} onChange={e => setEmpForm({ ...empForm, active: e.target.checked })} className="w-4 h-4 accent-[#16a34a]" />
                  <label htmlFor="active" className="text-sm text-gray-700">Empleado activo</label>
                </div>
                <div className="col-span-2"><label className="block text-xs font-medium text-gray-700 mb-1">Notas</label>
                  <textarea value={empForm.notes} onChange={e => setEmpForm({ ...empForm, notes: e.target.value })} rows={2} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none resize-none" /></div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">Cancelar</button>
                <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-[#16a34a] text-white rounded-lg hover:bg-[#15803d]" data-testid="save-employee-btn">{saving ? 'Guardando...' : 'Guardar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Attendance Modal */}
      {showAttModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>Registrar Asistencia</h2>
              <button onClick={() => setShowAttModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <form onSubmit={handleSaveAtt} className="p-5 space-y-4">
              <div><label className="block text-xs font-medium text-gray-700 mb-1">Empleado *</label>
                <select required value={attForm.employee_id} onChange={e => setAttForm({ ...attForm, employee_id: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none bg-white">
                  <option value="">Seleccionar empleado...</option>
                  {employees.filter(e => e.active).map(emp => <option key={emp.id} value={emp.id}>{emp.full_name}</option>)}
                </select></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-xs font-medium text-gray-700 mb-1">Fecha *</label>
                  <input type="date" required value={attForm.date} onChange={e => setAttForm({ ...attForm, date: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none" /></div>
                <div><label className="block text-xs font-medium text-gray-700 mb-1">Tipo</label>
                  <select value={attForm.type} onChange={e => setAttForm({ ...attForm, type: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none bg-white">
                    {Object.entries(ATT_TYPES).map(([k, v]) => <option key={k} value={k}>{v.l}</option>)}
                  </select></div>
                <div><label className="block text-xs font-medium text-gray-700 mb-1">Hora entrada</label>
                  <input type="time" value={attForm.start_time} onChange={e => setAttForm({ ...attForm, start_time: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none" /></div>
                <div><label className="block text-xs font-medium text-gray-700 mb-1">Hora salida</label>
                  <input type="time" value={attForm.end_time} onChange={e => setAttForm({ ...attForm, end_time: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none" /></div>
              </div>
              <div><label className="block text-xs font-medium text-gray-700 mb-1">Notas</label>
                <input type="text" value={attForm.notes} onChange={e => setAttForm({ ...attForm, notes: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none" /></div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowAttModal(false)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">Cancelar</button>
                <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-[#16a34a] text-white rounded-lg hover:bg-[#15803d]" data-testid="save-attendance-btn">{saving ? 'Guardando...' : 'Guardar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
